/**
 * @author Jorge Casas
 * @creationDate 10/10/2025
 * @path src\components\dashboard\ChartCostCentersAmount.js
 */
import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import { translate } from 'react-i18next';
import PropTypes from 'prop-types';

import { Bar } from 'react-chartjs-2';
import Chart from 'chart.js';
import 'chartjs-plugin-datalabels';
import * as Common from 'common/components';
import { formatMoney } from 'accounting-js';

let ROUNDED_PATCH_DONE = false;
let FANCY_PLUGINS_PATCHED = false;

function patchRoundedBarsOnce() {
  if (ROUNDED_PATCH_DONE) return;
  if (!Chart || !Chart.elements || !Chart.elements.Rectangle) return;
  ROUNDED_PATCH_DONE = true;

  Chart.elements.Rectangle.prototype.draw = function () {
    const ctx = this._chart.ctx;
    const vm = this._view;

    const left = vm.x - vm.width / 2;
    const right = vm.x + vm.width / 2;
    const top = vm.y;
    const bottom = vm.base;

    const radius = Math.max(
      0,
      Math.min(8, Math.min(Math.abs(right - left) / 2, Math.abs(bottom - top) / 2))
    );
    const r = radius;

    ctx.save();
    ctx.shadowColor = 'rgba(15, 23, 42, 0.12)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;

    ctx.beginPath();
    ctx.moveTo(left, bottom);
    ctx.lineTo(left, top + r);
    ctx.quadraticCurveTo(left, top, left + r, top);
    ctx.lineTo(right - r, top);
    ctx.quadraticCurveTo(right, top, right, top + r);
    ctx.lineTo(right, bottom);
    ctx.closePath();

    ctx.fillStyle = vm.backgroundColor;
    ctx.fill();

    if (vm.borderWidth) {
      ctx.strokeStyle = vm.borderColor;
      ctx.lineWidth = vm.borderWidth;
      ctx.stroke();
    }
    ctx.restore();
  };
}

function patchFancyPluginsOnce() {
  if (FANCY_PLUGINS_PATCHED) return;
  FANCY_PLUGINS_PATCHED = true;

  Chart.plugins.register({
    beforeDraw(chart) {
      const ca = chart.chartArea;
      const ctx = chart.ctx;
      if (!ca) return;
      const grad = ctx.createLinearGradient(0, ca.top, 0, ca.bottom);
      grad.addColorStop(0, 'rgba(59,130,246,0.06)');
      grad.addColorStop(1, 'rgba(20,184,166,0.02)');
      ctx.save();
      ctx.fillStyle = grad;
      ctx.fillRect(ca.left, ca.top, ca.right - ca.left, ca.bottom - ca.top);
      ctx.restore();
    },
  });

  Chart.plugins.register({
    afterDraw(chart) {
      const active = chart.tooltip && chart.tooltip._active && chart.tooltip._active[0];
      if (!active || !chart.chartArea) return;
      const x = active.tooltipPosition().x;
      const ca = chart.chartArea;
      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, ca.top);
      ctx.lineTo(x, ca.bottom);
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(51,65,85,0.22)';
      ctx.stroke();
      ctx.restore();
    },
  });

  Chart.plugins.register({
    afterDatasetsDraw(chart) {
      const ds = chart.config.data.datasets && chart.config.data.datasets[0];
      if (!ds || typeof ds.__avg !== 'number') return;
      const yScale = chart.scales['y-axis-0'] || chart.scales['y-axis-1'];
      if (!yScale) return;
      const y = yScale.getPixelForValue(ds.__avg);
      const ca = chart.chartArea;
      const ctx = chart.ctx;

      ctx.save();
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(99,102,241,0.6)';
      ctx.beginPath();
      ctx.moveTo(ca.left, y);
      ctx.lineTo(ca.right, y);
      ctx.stroke();
      ctx.setLineDash([]);

      const label = 'Promedio ' + formatMoney(ds.__avg);
      ctx.font = '11px Inter, system-ui, sans-serif';
      const padX = 8,
        h = 20;
      const w = ctx.measureText(label).width + padX * 2;
      const x = ca.right - w - 6;
      const yTop = y - h - 4;

      ctx.fillStyle = 'rgba(15,23,42,0.92)';
      ctx.fillRect(x, yTop, w, h);
      ctx.fillStyle = '#fff';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x + padX, yTop + h / 2);
      ctx.restore();
    },
  });
}

class ChartAccountsTotal extends Component {
  static propTypes = { data: PropTypes.array.isRequired, t: PropTypes.func };

  constructor(props) {
    super(props);
    patchRoundedBarsOnce();
    patchFancyPluginsOnce();
  }

  state = { mode: 'abs' };

  shouldComponentUpdate = (nProps, nState) =>
    !isEqual(nProps.data, this.props.data) || !isEqual(nState, this.state);

  hash = (s) => {
    let h = 0,
      str = String(s || '');
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };
  hslToRgb = (h, s, l) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s,
        p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  };
  colorFromKey = (key, alpha = 1) => {
    const H = this.hash(key);
    const hue = (H % 360) / 360,
      sat = 0.62 + ((H >> 8) % 3) * 0.06,
      lig = 0.52 + ((H >> 11) % 3) * 0.05;
    const rgb = this.hslToRgb(hue, Math.min(sat, 0.85), Math.min(lig, 0.7));
    return 'rgba(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ',' + alpha + ')';
  };
  colorKeyFor = (row) => row && (row._id || row.id || row.code || row.name || '');

  chartRef = null;
  setChartRef = (ref) => {
    this.chartRef = ref ? ref.chartInstance || ref.chart || ref : null;
  };
  downloadPNG = () => {
    const c = this.chartRef;
    if (!c || !c.toBase64Image) return;
    const a = document.createElement('a');
    a.href = c.toBase64Image();
    a.download = 'monto_por_centro_de_costo.png';
    a.click();
  };

  getChartData = (canvas) => {
    const ctx = canvas.getContext('2d');
    const H = canvas.height;

    const rows = (this.props.data || [])
      .filter(function (r) {
        return r && r.total > 0;
      })
      .map((r) => ({
        label: this.props.t ? this.props.t(r.name) : String(r.name || ''),
        value: r.total || 0,
        key: this.colorKeyFor(r),
      }))
      .sort(function (a, b) {
        return (b.value || 0) - (a.value || 0);
      });

    const sumAll =
      rows.reduce(function (a, b) {
        return a + (b.value || 0);
      }, 0) || 1;
    const avg = rows.length
      ? rows.reduce(function (a, b) {
          return a + (b.value || 0);
        }, 0) / rows.length
      : 0;

    const labels = [];
    const values = [];
    const bgs = [];
    const borders = [];
    const hoverBgs = [];

    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      labels.push(r.label);
      var base = this.colorFromKey(r.key, 1);
      var grad = ctx.createLinearGradient(0, H, 0, 0);
      grad.addColorStop(0, this.colorFromKey(r.key, 0.55));
      grad.addColorStop(1, this.colorFromKey(r.key, 0.92));
      bgs.push(grad);
      borders.push(base);
      hoverBgs.push(base);
      values.push(this.state.mode === 'pct' ? +((r.value * 100) / sumAll).toFixed(1) : r.value);
    }

    const topIdx = Array.from({ length: Math.min(3, rows.length) }, function (_, i) {
      return i;
    });
    const top1 = rows.length ? [0] : [];

    return {
      labels: labels,
      datasets: [
        {
          label:
            this.state.mode === 'pct'
              ? '% del total'
              : this.props.t
              ? this.props.t('dashboard.amountTotal')
              : 'Monto total',
          data: values,
          backgroundColor: bgs,
          borderColor: borders,
          hoverBackgroundColor: hoverBgs,
          borderWidth: 0,
          maxBarThickness: 28,
          categoryPercentage: 0.7,
          barPercentage: 0.9,
          extraTopIndices: topIdx,
          extraHighlightIndices: top1,
          __sumAll: sumAll,
          __avg: avg,
          __mapRaw: rows,
          __mode: this.state.mode,
        },
      ],
    };
  };

  render() {
    const t =
      this.props.t ||
      function (s) {
        return s;
      };

    const dataArr = this.props.data || [];
    const total = dataArr.reduce(function (a, b) {
      return a + ((b && b.total) || 0);
    }, 0);
    const cuentas = dataArr.length;
    const ordered = dataArr.slice().sort(function (a, b) {
      return ((b && b.total) || 0) - ((a && a.total) || 0);
    });
    const top1 = ordered[0];
    const topShare = top1 ? (((top1.total || 0) / Math.max(total, 1)) * 100).toFixed(1) : '0.0';

    return (
      <div className="col-lg-12">
        <Common.Portlet title={t('dashboard.amountByCostCenters')}>
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap chart-toolbar">
            <div className="d-flex align-items-center flex-wrap">
              <span className="badge badge-light mr-2 mb-2">Total: {formatMoney(total)}</span>
              <span className="badge badge-light mr-2 mb-2">Centros: {cuentas}</span>
              <span className="badge badge-light mr-2 mb-2">Top concentra: {topShare}%</span>
            </div>

            <div className="d-flex align-items-center flex-wrap mt-2 mt-sm-0" style={{ gap: 10 }}>
              <div
                className="segmented-toggle mb-2"
                role="group"
                aria-label="Modo de visualización"
              >
                <button
                  type="button"
                  className={'segmented-btn ' + (this.state.mode === 'abs' ? 'is-active' : '')}
                  aria-pressed={this.state.mode === 'abs'}
                  onClick={() => this.setState({ mode: 'abs' })}
                  title="Mostrar valores absolutos"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M5 9h14M5 15h14M9 3l-2 18M17 3l-2 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>ABS</span>
                </button>

                <button
                  type="button"
                  className={'segmented-btn ' + (this.state.mode === 'pct' ? 'is-active' : '')}
                  aria-pressed={this.state.mode === 'pct'}
                  onClick={() => this.setState({ mode: 'pct' })}
                  title="Mostrar porcentaje del total"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M19 5L5 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle cx="7.5" cy="6.5" r="2" stroke="currentColor" strokeWidth="2" />
                    <circle cx="16.5" cy="17.5" r="2" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>%</span>
                </button>
              </div>

              <button
                type="button"
                className="download-btn--pro mb-2"
                aria-label="Descargar gráfico en PNG"
                onClick={this.downloadPNG}
                title="Descargar imagen PNG"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M12 3v10m0 0l-4-4m4 4l4-4M5 21h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="btn-text" aria-hidden="true">
                  Descargar PNG
                </span>
              </button>
            </div>
          </div>

          <div style={{ position: 'relative', height: 380, maxHeight: '65vh' }}>
            <Bar
              ref={(ref) => {
                this.chartRef = ref ? ref.chartInstance || ref.chart || ref : null;
              }}
              data={this.getChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 16, right: 12, left: 8, bottom: 8 } },
                animation: { duration: 900, easing: 'easeOutQuart' },
                hover: { mode: 'index', intersect: false },
                onHover: (e) => {
                  e.target.style.cursor = 'default';
                },
                onClick: () => {},

                plugins: {
                  datalabels: {
                    display: function (ctx) {
                      const arr = ctx.dataset.extraTopIndices || [];
                      return arr.indexOf(ctx.dataIndex) > -1;
                    },
                    anchor: 'end',
                    align: 'end',
                    offset: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(15,23,42,0.88)',
                    color: '#fff',
                    padding: { top: 3, bottom: 3, left: 6, right: 6 },
                    formatter: (v, ctx) => {
                      const mode = ctx && ctx.dataset && ctx.dataset.__mode;
                      if (mode === 'pct') return v + '%';
                      return formatMoney(v);
                    },
                    font: { size: 11, weight: '600' },
                  },
                },
                elements: { rectangle: { borderSkipped: 'bottom' } },
                legend: { labels: { boxWidth: 14, usePointStyle: true, fontColor: '#334155' } },
                tooltips: {
                  backgroundColor: 'rgba(15, 23, 42, 0.92)',
                  titleFontStyle: 'normal',
                  xPadding: 10,
                  yPadding: 10,
                  cornerRadius: 6,
                  mode: 'index',
                  intersect: false,
                  callbacks: {
                    title: function (tooltipItems, data) {
                      var i = tooltipItems && tooltipItems[0] ? tooltipItems[0].index : 0;
                      return data.labels[i];
                    },
                    label: (ti, data) => {
                      const ds = data.datasets[ti.datasetIndex];
                      const vShown = ds.data[ti.index];
                      return this.state.mode === 'pct'
                        ? ' ' + vShown + '%'
                        : ' ' + formatMoney(vShown);
                    },
                    afterLabel: (ti, data) => {
                      const ds = data.datasets[ti.datasetIndex];
                      const rawList = ds.__mapRaw || [];
                      const raw = rawList[ti.index] ? rawList[ti.index].value : 0;
                      const sumAll = ds.__sumAll || 1;
                      const pctAll = ((raw / sumAll) * 100).toFixed(1);
                      return this.state.mode === 'pct'
                        ? ' ' + formatMoney(raw) + ' (ABS)'
                        : ' ' + pctAll + '% del total';
                    },
                  },
                },
                scales: {
                  xAxes: [
                    {
                      ticks: {
                        autoSkip: false,
                        minRotation: 60,
                        maxRotation: 60,
                        fontSize: 11,
                        fontColor: '#334155',
                        callback: function (value) {
                          const s = String(value || '');
                          const max = 18;
                          return s.length > max ? s.substr(0, max - 1) + '…' : s;
                        },
                      },
                      gridLines: { display: false },
                    },
                  ],
                  yAxes: [
                    {
                      ticks: {
                        beginAtZero: true,
                        padding: 8,
                        fontSize: 11,
                        fontColor: '#475569',
                        userCallback: (v) => (this.state.mode === 'pct' ? v + '%' : formatMoney(v)),
                      },
                      gridLines: {
                        drawBorder: false,
                        drawTicks: false,
                        color: 'rgba(148,163,184,0.18)',
                        zeroLineColor: 'rgba(148,163,184,0.30)',
                        borderDash: [4, 4],
                        zeroLineBorderDash: [4, 4],
                      },
                    },
                  ],
                },
              }}
            />
          </div>
        </Common.Portlet>
      </div>
    );
  }
}

export default translate()(ChartAccountsTotal);



// WEBPACK FOOTER //
// src/components/dashboard/ChartCostCentersAmount.js